/**
 * #sed-owner-path — the ONE place a SED's GCS object key is built.
 *
 * A SED lives under the site OWNER's namespace, and the site doc's
 * `sedStoragePath` is the canonical key. Building the key from the CALLER's
 * uid instead silently FORKS a shared site: a company member's write lands in
 * `users/{their-uid}/…` while the canonical object under the owner never
 * changes — and because the caller still bumps `site.sedVersion`, every
 * staleness check believes the write landed. Measured on Redhead 2026-07-27:
 * pointer at v18, canonical file at v14, 40+ stray SED objects across three
 * uid namespaces for ONE site.
 *
 * The read side (`sedLoader.loadSedById`, `sedBootstrap.loadSedFromGcs`)
 * always tries `site.sedStoragePath` first, so any writer that does not
 * resolve through this module can (and did) write to an object nobody reads.
 *
 * Two resolvers, because the two write shapes want different things from a
 * legacy-layout site:
 *
 *   • `resolveSedSavePath`         — for a REBUILD that mints a NEW sed id.
 *     Always emits the modern site-nested layout, so a legacy site migrates
 *     forward. The caller MUST persist the returned path back to
 *     `site.sedStoragePath` (bootstrap does).
 *
 *   • `resolveSedInPlaceSavePath`  — for an in-place reference of the sed the
 *     site is ALREADY pointing at (amendments, conflict resolutions, top-up
 *     baselines). Keeps the canonical object's LAYOUT (including immutable
 *     site-nested revision pointers `.../evidence/revisions/{sedId}/{revId}.json`
 *     when `sedId` matches) so callers address the exact object the read side
 *     loads.
 *
 * Pure: no I/O, no firebase, no `authedFetch`. Safe to import from client
 * services, components, the store, and Node-side jobs alike.
 */

/** `users/{ownerUid}/sites/{siteId}/evidence/{sedId}.json` — modern layout. */
export function sedObjectPath(uid: string, siteId: string, sedId: string): string {
  return `users/${uid}/sites/${siteId}/evidence/${sedId}.json`;
}

/** `users/{ownerUid}/sites/{siteId}/evidence/revisions/{sedId}/{revisionId}.json` — immutable revision layout. */
export function sedRevisionObjectPath(
  uid: string,
  siteId: string,
  sedId: string,
  revisionId: string,
): string {
  return `users/${uid}/sites/${siteId}/evidence/revisions/${sedId}/${revisionId}.json`;
}

/** `users/{ownerUid}/evidence/{sedId}.json` — legacy flat layout. */
export function legacySedObjectPath(uid: string, sedId: string): string {
  return `users/${uid}/evidence/${sedId}.json`;
}

/** Which of the two historical object layouts a canonical path uses. */
export type SedStorageLayout = 'site-nested' | 'legacy-flat';

export interface SedStorageAnchor {
  /** The uid whose namespace actually holds this site's SED objects. */
  ownerUid: string;
  layout: SedStorageLayout;
}

export interface SedRevisionStorageDescriptor {
  ownerUid: string;
  siteId: string;
  sedId: string;
  revisionId: string;
}

const SITE_NESTED_FILE_RE = /^users\/([^/]+)\/sites\/([^/]+)\/evidence\/([^/]+)\.json$/;
const SITE_NESTED_REVISION_RE =
  /^users\/([^/]+)\/sites\/([^/]+)\/evidence\/revisions\/([^/]+)\/([^/]+)\.json$/;
const LEGACY_FLAT_RE = /^users\/([^/]+)\/evidence\/([^/]+)\.json$/;

function isValidPathSegment(seg: string | undefined): seg is string {
  return typeof seg === 'string' && seg.length > 0 && seg !== '.' && seg !== '..';
}

/**
 * Parse a site-nested immutable revision path
 * (`users/{ownerUid}/sites/{siteId}/evidence/revisions/{sedId}/{revisionId}.json`).
 */
export function isPublishedStagedSedRevisionPath(
  storagePath: string | null | undefined,
  expectedSiteId?: string,
  expectedSedId?: string,
): boolean {
  const parsed = parseSedRevisionStoragePath(storagePath, expectedSiteId);
  if (!parsed) return false;
  if (expectedSedId !== undefined && parsed.sedId !== expectedSedId) return false;
  return true;
}

export function parseSedRevisionStoragePath(
  storagePath: string | null | undefined,
  expectedSiteId?: string,
): SedRevisionStorageDescriptor | null {
  if (typeof storagePath !== 'string' || storagePath.length === 0) return null;
  if (storagePath.includes('..')) return null;
  const match = storagePath.match(SITE_NESTED_REVISION_RE);
  if (!match) return null;
  const [, ownerUid, siteId, sedId, revisionId] = match;
  if (
    !isValidPathSegment(ownerUid) ||
    !isValidPathSegment(siteId) ||
    !isValidPathSegment(sedId) ||
    !isValidPathSegment(revisionId)
  ) {
    return null;
  }
  if (expectedSiteId !== undefined && siteId !== expectedSiteId) {
    return null;
  }
  return { ownerUid, siteId, sedId, revisionId };
}

/**
 * Read the owner + layout out of a site's canonical `sedStoragePath`.
 *
 * Returns `null` — meaning "no trustworthy anchor, fall back to the caller" —
 * when the path is absent, malformed, contains a traversal segment, or is a
 * site-nested path for a DIFFERENT site (a mismatched pointer must never
 * redirect this site's write into another site's folder).
 *
 * Supports both plain site-nested paths (`.../evidence/{sedId}.json`) and
 * immutable site-nested revision paths (`.../evidence/revisions/{sedId}/{revId}.json`),
 * while continuing to reject legacy flat revision paths (`users/{uid}/evidence/{sedId}/revisions/...`).
 */
export function parseSedStorageAnchor(
  canonicalStoragePath: string | null | undefined,
  siteId: string,
): SedStorageAnchor | null {
  if (typeof canonicalStoragePath !== 'string' || canonicalStoragePath.length === 0) return null;
  if (canonicalStoragePath.includes('..')) return null;

  const nestedFile = canonicalStoragePath.match(SITE_NESTED_FILE_RE);
  if (nestedFile) {
    if (!isValidPathSegment(nestedFile[1]) || !isValidPathSegment(nestedFile[2]) || !isValidPathSegment(nestedFile[3])) {
      return null;
    }
    if (nestedFile[2] !== siteId) return null;
    return { ownerUid: nestedFile[1], layout: 'site-nested' };
  }

  const nestedRevision = parseSedRevisionStoragePath(canonicalStoragePath, siteId);
  if (nestedRevision) {
    return { ownerUid: nestedRevision.ownerUid, layout: 'site-nested' };
  }

  const flat = canonicalStoragePath.match(LEGACY_FLAT_RE);
  if (flat && isValidPathSegment(flat[1]) && isValidPathSegment(flat[2])) {
    return { ownerUid: flat[1], layout: 'legacy-flat' };
  }

  return null;
}

/**
 * MIGRATING resolver — owner segment from the canonical path, modern
 * site-nested layout always, object name from the CURRENT sed id.
 *
 * Use when the sed id is new (a rebuild), and persist the result to
 * `site.sedStoragePath` after a successful save.
 */
export function resolveSedSavePath(
  callerUid: string,
  siteId: string,
  sedId: string,
  canonicalStoragePath?: string | null,
): string {
  const anchor = parseSedStorageAnchor(canonicalStoragePath, siteId);
  return sedObjectPath(anchor?.ownerUid ?? callerUid, siteId, sedId);
}

/**
 * IN-PLACE resolver — owner segment AND layout from the canonical path,
 * object name from the CURRENT sed id.
 *
 * When `canonicalStoragePath` is already an immutable site-nested revision
 * path (`users/{ownerUid}/sites/{siteId}/evidence/revisions/{sedId}/{revId}.json`)
 * for the SAME `sedId`, preserves that exact revision path so callers
 * (`topUpSiteSed`, `saveSedById`, `persistSedConflicts`) reference the live
 * revision pointer rather than reconstructing a stale plain filename.
 */
export function resolveSedInPlaceSavePath(
  callerUid: string,
  siteId: string,
  sedId: string,
  canonicalStoragePath?: string | null,
): string {
  const revision = parseSedRevisionStoragePath(canonicalStoragePath, siteId);
  if (revision && revision.sedId === sedId) {
    return sedRevisionObjectPath(revision.ownerUid, siteId, sedId, revision.revisionId);
  }
  const anchor = parseSedStorageAnchor(canonicalStoragePath, siteId);
  if (!anchor) return sedObjectPath(callerUid, siteId, sedId);
  return anchor.layout === 'legacy-flat'
    ? legacySedObjectPath(anchor.ownerUid, sedId)
    : sedObjectPath(anchor.ownerUid, siteId, sedId);
}
